import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Camera, Loader2, Check, PlusCircle, Trash2, ImageIcon, FileText } from "lucide-react";
import { cn, formatCurrency } from "@/lib/utils";
import type { ImportSessionViewProps } from "@/ui/view-props";

export function ImportSessionClassicView(p: ImportSessionViewProps) {
  if (p.step === 1) {
    return (
      <div className="py-6 space-y-4">
        <div className="flex items-center gap-2">
          <Button size="icon" variant="ghost" onClick={p.navigateHome}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold tracking-tight">Import Session</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Upload a photo or paste OCR text from a score sheet
        </p>

        {p.previewUrl && (
          <Card className="border-0 shadow-sm overflow-hidden">
            <img
              src={p.previewUrl}
              alt="Score sheet"
              className="w-full max-h-64 object-contain bg-muted"
            />
          </Card>
        )}

        {p.loading && (
          <div className="flex flex-col items-center gap-3 py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Processing your data...</p>
          </div>
        )}

        {p.error && (
          <Card className="border-destructive bg-destructive/10">
            <CardContent className="p-4 space-y-3">
              <p className="text-sm text-destructive">{p.error}</p>
              {p.rawDebug && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground">
                    Show raw AI response
                  </summary>
                  <pre className="mt-2 p-2 bg-muted rounded text-foreground overflow-x-auto whitespace-pre-wrap">
                    {p.rawDebug}
                  </pre>
                </details>
              )}
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={p.onTryAgain}>
                  Try Again
                </Button>
                <Button variant="secondary" size="sm" onClick={p.onManualEntry}>
                  Enter Manually
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {!p.loading && !p.error && (
          <div className="space-y-4">
            {p.settings.enable_vision && (
              <div className="grid grid-cols-2 gap-3">
                <Button
                  className="h-14 text-sm font-semibold gap-2"
                  onClick={p.onTakePhotoClick}
                >
                  <Camera className="h-5 w-5" />
                  Take Photo
                </Button>
                <Button
                  variant="secondary"
                  className="h-14 text-sm font-semibold gap-2"
                  onClick={p.onLibraryClick}
                >
                  <ImageIcon className="h-5 w-5" />
                  Library
                </Button>
              </div>
            )}

            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="ocr-text" className="text-sm font-medium">
                  Paste OCR Text
                </Label>
                <Textarea
                  id="ocr-text"
                  placeholder="Paste text from Google Lens or other OCR tools here..."
                  className="min-h-[120px] bg-muted/50"
                  value={p.rawText}
                  onChange={(e) => p.setRawText(e.target.value)}
                />
              </div>

              {/* Buy-in picker */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Buy-in amount</Label>
                <div className="flex gap-2 flex-wrap items-center">
                  {[10, 20, 50, 100].map((preset) => (
                    <Button
                      key={preset}
                      size="sm"
                      variant={p.buyInUnit === preset ? 'default' : 'outline'}
                      onClick={() => p.setBuyInUnit(preset)}
                      data-testid={`buyin-preset-${preset}`}
                    >
                      {preset}
                    </Button>
                  ))}
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    value={p.buyInUnit}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      if (v > 0) p.setBuyInUnit(v);
                    }}
                    className="w-24 h-9"
                    data-testid="buyin-custom-input"
                  />
                </div>
              </div>

              <Button
                variant="secondary"
                className="w-full h-12 text-sm font-semibold gap-2"
                onClick={p.onTextProcess}
                disabled={!p.rawText.trim()}
              >
                <FileText className="h-5 w-5" />
                Process Text
              </Button>
            </div>

            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or</span>
              </div>
            </div>

            <Button
              variant="outline"
              className="w-full h-12 text-sm font-semibold"
              onClick={p.onManualEntry}
            >
              Enter Data Manually
            </Button>
          </div>
        )}
      </div>
    );
  }

  if (p.step === 2 && p.data) {
    return (
      <div className="py-6 space-y-4">
        <div className="flex items-center gap-2">
          <Button size="icon" variant="ghost" onClick={() => p.setStep(1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold tracking-tight">Review Extracted Data</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Yellow rows have an entry mismatch. Edit any field to correct.
        </p>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Date:</span>
          <Input
            type="date"
            value={p.data.sessionDate || ""}
            onChange={(e) => p.setData({ ...p.data!, sessionDate: e.target.value })}
            className="h-9 w-auto"
          />
        </div>

        <datalist id="existing-players">
          {p.allPlayers?.map((player) => (
            <option key={player.id} value={player.name} />
          ))}
        </datalist>

        {p.hasDuplicateNames() && (
          <Card className="border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20">
            <CardContent className="p-3">
              <p className="text-sm text-yellow-700 dark:text-yellow-400 font-medium">
                ⚠ Duplicate player names detected. Each player must have a unique name.
              </p>
            </CardContent>
          </Card>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-1 font-medium text-muted-foreground">
                  Player
                </th>
                <th className="text-center py-2 px-1 font-medium text-muted-foreground">
                  Entries
                </th>
                <th className="text-center py-2 px-1 font-medium text-muted-foreground">
                  Results
                </th>
                <th className="text-center py-2 px-1 font-medium text-muted-foreground">
                  Net
                </th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {p.data.players.map((row, i) => {
                const dupes = p.getDuplicateNames();
                const isDupe = row.name.trim() && dupes.has(row.name.trim().toLowerCase());
                return (
                  <tr
                    key={i}
                    className={cn(
                      "border-b border-border/50",
                      isDupe && "bg-red-50 dark:bg-red-900/20",
                      !isDupe && Math.abs(row.variance) > 0.01 && "bg-yellow-50 dark:bg-yellow-900/20"
                    )}
                  >
                    <td className="py-1 px-1">
                      <Input
                        value={row.name}
                        placeholder="Name"
                        list="existing-players"
                        onChange={(e) => p.updatePlayer(i, "name", e.target.value)}
                        className={cn("h-9 text-sm", isDupe && "border-red-400")}
                      />
                    </td>
                    <td className="py-1 px-1">
                      <Input
                        type="number"
                        inputMode="numeric"
                        value={row.buyinCount}
                        onChange={(e) => p.updatePlayer(i, "buyinCount", e.target.value)}
                        className="h-9 text-sm text-center w-16"
                      />
                    </td>
                    <td className="py-1 px-1">
                      <Input
                        type="number"
                        inputMode="decimal"
                        value={row.cashoutAmount}
                        onChange={(e) => p.updatePlayer(i, "cashoutAmount", e.target.value)}
                        className="h-9 text-sm text-center w-20"
                      />
                    </td>
                    <td className="py-1 px-1">
                      <Input
                        type="number"
                        inputMode="decimal"
                        value={row.net}
                        onChange={(e) => p.updatePlayer(i, "net", e.target.value)}
                        className={cn(
                          "h-9 text-sm text-center w-20 font-bold",
                          row.net > 0 ? "text-success" : row.net < 0 ? "text-destructive" : ""
                        )}
                      />
                    </td>
                    <td className="py-1 px-1">
                      {p.data!.players.length > 1 && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => p.removePlayer(i)}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <Button variant="outline" size="sm" className="gap-1" onClick={p.addPlayer}>
          <PlusCircle className="h-4 w-4" />
          Add Player
        </Button>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-3 space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Pot</span>
              <span className="font-mono font-bold">{formatCurrency(p.data.totalPot)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Won</span>
              <span className="font-mono font-bold text-success">
                +{formatCurrency(p.data.totalPositive)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Lost</span>
              <span className="font-mono font-bold text-destructive">
                {formatCurrency(p.data.totalNegative)}
              </span>
            </div>
            {Math.abs(p.data.variance) > 0.01 && (
              <div className="flex justify-between text-yellow-600 gap-2">
                <span title="Sum of all player nets; should be 0 when the sheet balances.">
                  Net Variance
                </span>
                <span className="font-mono font-bold shrink-0">
                  {formatCurrency(p.data.variance)}
                </span>
              </div>
            )}
            {p.data.adjustmentNote && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Note</span>
                <span className="text-xs">{p.data.adjustmentNote}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Button
          className="w-full h-14 text-base font-semibold gap-2"
          onClick={() => p.setStep(3)}
          disabled={p.hasDuplicateNames()}
        >
          Looks Good — Continue
        </Button>
      </div>
    );
  }

  if (p.step === 3 && p.data) {
    return (
      <div className="py-6 space-y-4">
        <div className="flex items-center gap-2">
          <Button size="icon" variant="ghost" onClick={() => p.setStep(2)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold tracking-tight">Confirm Session</h1>
        </div>

        {p.data.sessionDate && (
          <p className="text-sm text-muted-foreground">
            Session Date:{" "}
            <span className="font-medium text-foreground">{p.data.sessionDate}</span>
          </p>
        )}

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-1.5 font-medium text-muted-foreground">
                    Player
                  </th>
                  <th className="text-right py-1.5 font-medium text-muted-foreground">
                    Entries
                  </th>
                  <th className="text-right py-1.5 font-medium text-muted-foreground">
                    Results
                  </th>
                  <th className="text-right py-1.5 font-medium text-muted-foreground">
                    Net
                  </th>
                </tr>
              </thead>
              <tbody>
                {p.data.players
                  .filter((r) => r.name.trim())
                  .map((r, i) => (
                    <tr key={i} className="border-b border-border/50 last:border-0">
                      <td className="py-1.5 font-medium">{r.name}</td>
                      <td className="py-1.5 text-right font-mono">{r.buyinCount}</td>
                      <td className="py-1.5 text-right font-mono">
                        {formatCurrency(r.cashoutAmount)}
                      </td>
                      <td
                        className={cn(
                          "py-1.5 text-right font-mono font-bold",
                          r.net > 0
                            ? "text-success"
                            : r.net < 0
                              ? "text-destructive"
                              : "text-muted-foreground"
                        )}
                      >
                        {r.net >= 0 ? "+" : ""}
                        {formatCurrency(r.net)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-3 space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Pot</span>
              <span className="font-mono font-bold">{formatCurrency(p.data.totalPot)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Won</span>
              <span className="font-mono font-bold text-success">
                +{formatCurrency(p.data.totalPositive)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Lost</span>
              <span className="font-mono font-bold text-destructive">
                {formatCurrency(p.data.totalNegative)}
              </span>
            </div>
            {Math.abs(p.data.variance) > 0.01 && (
              <div className="flex justify-between text-yellow-600 gap-2">
                <span title="Sum of all player nets; should be 0 when the sheet balances.">
                  Net Variance
                </span>
                <span className="font-mono font-bold shrink-0">
                  {formatCurrency(p.data.variance)}
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        <Button
          className="w-full h-14 text-base font-semibold gap-2"
          onClick={p.saveSession}
          disabled={p.saving}
        >
          {p.saving ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Check className="h-5 w-5" />
          )}
          Create Session
        </Button>
      </div>
    );
  }

  return null;
}

